/**
 * amphr-cart.js — Progressive AJAX enhancement for the amphr cart drawer.
 * Loaded after amphr.js (defer). Works only when JS is available;
 * the server-rendered cart remains fully functional without it.
 *
 * Responsibilities:
 *   1. Intercept "Add to cart" (quick-add buttons + PDP form)
 *   2. Intercept qty change / remove inside #amphr-cart drawer
 *   3. applyCart()/refreshCart() — rebuild drawer DOM from cart JSON + update badge
 *
 * i18n: all user-facing strings come from window.amphrStrings (set in
 * layout/theme.liquid via Liquid | t). English literals are last-resort fallbacks.
 * Routes: all endpoints derive from window.routes (locale-prefix safe).
 */

(function () {
  'use strict';

  /* ─── Helpers ──────────────────────────────────────────────────────────── */
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };

  var routes = window.routes || {};
  var CART_URL = (routes.cart_url || '/cart') + '.js';
  var CART_ADD_URL = (routes.cart_add_url || '/cart/add') + '.js';
  var CART_CHANGE_URL = (routes.cart_change_url || '/cart/change') + '.js';
  var ALL_PRODUCTS_URL = routes.all_products_collection_url || '/collections/all';

  var S = window.amphrStrings || {};
  function t(key, fallback, vars) {
    var str = S[key] || fallback;
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        str = str.replace('{{ ' + k + ' }}', vars[k]).replace('{{' + k + '}}', vars[k]);
      });
    }
    return str;
  }

  /* ─── Money formatting ─────────────────────────────────────────────────── */
  /**
   * Format an integer (sub)unit amount using window.amphrMoneyFormat.
   * Handles the common Shopify money format tokens incl. thousands separators.
   */
  function formatMoney(cents) {
    var fmt = window.amphrMoneyFormat || '{{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? intPart + decimal + parts[1] : intPart;
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount': return withDelimiters(cents, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(cents, 2, "'", '.');
        default: return withDelimiters(cents, 2, ',', '.');
      }
    });
  }
  /* Markup, not text: shop.money_format is a merchant setting and has carried an
     HTML wrapper on this store before, which textContent then showed to the
     customer as literal tags. */
  function setMoney(el, cents) {
    el.innerHTML = formatMoney(cents);
  }

  // Shared money helpers for the rest of the theme (amphr.js variant picker etc.)
  if (window.amphr) {
    window.amphr.formatMoney = formatMoney;
    window.amphr.setMoney = setMoney;
  }

  /* ─── Error display ────────────────────────────────────────────────────── */
  function showError(nearEl, message) {
    var existing = nearEl.parentNode && nearEl.parentNode.querySelector('.amphr-cart-err');
    if (existing) existing.remove();

    var err = document.createElement('p');
    err.className = 'amphr-cart-err';
    err.setAttribute('role', 'alert');
    err.textContent = message || t('cart_error', 'Could not update your bag. Please try again.');

    if (nearEl.parentNode) {
      nearEl.parentNode.insertBefore(err, nearEl.nextSibling);
    }
    setTimeout(function () { if (err.parentNode) err.parentNode.removeChild(err); }, 4000);
  }

  /* Parse a fetch Response: reject with the server's error description on failure. */
  function parseCartResponse(r) {
    return r.json().catch(function () {
      throw new Error(t('cart_error', 'Could not update your bag. Please try again.'));
    }).then(function (data) {
      if (!r.ok || (data && data.status && data.status >= 400)) {
        throw new Error((data && (data.description || data.message)) || t('cart_error', 'Could not update your bag. Please try again.'));
      }
      return data;
    });
  }

  /* ─── updateFreeShip() ─────────────────────────────────────────────────── */
  function updateFreeShip(cart) {
    var bars = document.querySelectorAll('[data-freeship-bar]');
    if (!bars.length) return;

    var totalCents = cart.total_price || 0;

    bars.forEach(function (bar) {
      var thresholdCents = parseInt(bar.getAttribute('data-threshold'), 10);
      if (isNaN(thresholdCents) || thresholdCents <= 0) return;

      var remainingCents = thresholdCents - totalCents;
      var pct = Math.min(100, Math.round(totalCents * 100 / thresholdCents));

      var msgEl = bar.querySelector('[data-freeship-msg]');
      var fillEl = bar.querySelector('[data-freeship-fill]');

      if (remainingCents <= 0) {
        bar.classList.add('prog--met');
        if (msgEl) msgEl.textContent = t('freeship_unlocked', "You've unlocked free express shipping");
        if (fillEl) fillEl.style.width = '100%';
      } else {
        bar.classList.remove('prog--met');
        if (msgEl) msgEl.innerHTML = t('freeship_progress', "You're {{ remaining }} away from free express shipping", { remaining: formatMoney(remainingCents) });
        if (fillEl) fillEl.style.width = pct + '%';
      }
    });
  }

  /* ─── toggleDrawerBlocks() ─────────────────────────────────────────────── */
  function toggleDrawerBlocks(cart) {
    var isEmpty = !cart.item_count;

    document.querySelectorAll('[data-freeship-bar]').forEach(function (el) { el.hidden = isEmpty; });
    document.querySelectorAll('[data-gift-notice]').forEach(function (el) { el.hidden = isEmpty; });

    var recsEl = document.getElementById('amphr-drawer-recs');
    if (recsEl) recsEl.hidden = isEmpty;

    var footEl = document.getElementById('amphr-cart-foot');
    if (footEl) footEl.hidden = isEmpty;
  }

  /* ─── applyCart(cart) — single entry point for updating all cart UI ────── */
  /* Sequence guard: only the latest state may touch the DOM (out-of-order
     AJAX responses would otherwise repaint stale data). */
  var cartSeq = 0;

  /* Everything the drawer draws, in one string. A cart app's events arrive in
     bursts and several of them report a cart identical to the one on screen;
     rebuildDrawerBody tears the whole list down and builds it again, images
     included, so repainting on an unchanged cart is a visible flicker for no
     gain. Measured: one Remove produced four full rebuilds, the last 3s after
     the click. */
  function cartFingerprint(cart) {
    if (!cart) return '';
    return [cart.item_count, cart.total_price, cart.total_discount].concat(
      (cart.items || []).map(function (i) {
        return [i.key, i.quantity, i.final_line_price, i.original_line_price,
                (i.properties && i.properties._auto_added) || ''].join(':');
      })
    ).join('|');
  }

  var lastRendered = null;

  function applyCart(cart, seq) {
    if (seq !== undefined && seq !== cartSeq) return; // stale response — discard

    var fp = cartFingerprint(cart);
    if (fp === lastRendered) return;                  // nothing visible changed
    lastRendered = fp;

    rebuildDrawerBody(cart);
    rebuildDrawerFooter(cart);
    toggleDrawerBlocks(cart);
    updateTitle(cart);
    updateBadge(cart);
    updateFreeShip(cart);
    document.dispatchEvent(new CustomEvent('amphr:cart:refreshed', { detail: cart }));
    if (window.amphrCartRecs && typeof window.amphrCartRecs.refresh === 'function') {
      window.amphrCartRecs.refresh();
    }
  }

  /* ─── refreshCart() ────────────────────────────────────────────────────── */
  /* Coalescing: the app fires several events for one change, and each used to
     open its own /cart.js round-trip — eight requests for one Remove, measured.
     While one is in flight the others join it.

     A request that joins cannot simply take that answer, though: the cart may
     have changed AFTER the in-flight request was sent, and it would then settle
     on a state that is already stale. So a caller arriving mid-flight also asks
     for one more round after the current one lands. Worst case two requests,
     and the last one always reflects the final cart. */
  var inFlightRefresh = null;
  var refreshQueued = false;

  function refreshCart() {
    if (inFlightRefresh) {
      refreshQueued = true;
      return inFlightRefresh;
    }

    var mySeq = ++cartSeq;
    inFlightRefresh = fetch(CART_URL)
      .then(parseCartResponse)
      .then(function (cart) { applyCart(cart, mySeq); })
      .catch(function (err) {
        console.warn('[amphr-cart] refreshCart error', err);
      })
      .then(function () {
        inFlightRefresh = null;
        if (refreshQueued) {
          refreshQueued = false;
          return refreshCart();
        }
      });

    return inFlightRefresh;
  }

  /* ─── Rebuild drawer body ──────────────────────────────────────────────── */
  function rebuildDrawerBody(cart) {
    var db = $('#amphr-cart .db');
    if (!db) return;

    /* The recommendation rail lives inside .db (it scrolls with the lines), so
       clearing the body would take it with it. It is detached, the lines are
       rebuilt, then it goes back at the end. */
    var recs = db.querySelector('[data-cart-recs]');
    if (recs && recs.parentNode) recs.parentNode.removeChild(recs);

    while (db.firstChild) db.removeChild(db.firstChild);

    if (!cart.item_count) {
      var emptyDiv = document.createElement('div');
      emptyDiv.className = 'emptym';

      var emptyP = document.createElement('p');
      emptyP.textContent = t('bag_empty', 'Your bag is empty');
      emptyDiv.appendChild(emptyP);

      var shopLink = document.createElement('a');
      shopLink.href = ALL_PRODUCTS_URL;
      shopLink.className = 'atc';
      shopLink.setAttribute('data-close', '');
      shopLink.textContent = t('shop_the_range', 'Shop the range');
      emptyDiv.appendChild(shopLink);

      db.appendChild(emptyDiv);
      if (recs) db.appendChild(recs);
      return;
    }

    cart.items.forEach(function (item) {
      /* Same rule as cart-drawer.liquid: GiftPro's own line carries
         `_auto_added`, and it gets no controls because changing or removing it
         only makes the app put it back. */
      var isGift = !!(item.properties && String(item.properties._auto_added) === 'true');

      var miniDiv = document.createElement('div');
      miniDiv.className = isGift ? 'mini mini--gift' : 'mini';

      /* Image link */
      var imgLink = document.createElement('a');
      imgLink.className = 'mini__media';
      imgLink.href = item.url || '#';
      imgLink.setAttribute('tabindex', '-1');
      imgLink.setAttribute('aria-hidden', 'true');

      var imgUrl = item.image || (item.featured_image && item.featured_image.url) || '';
      if (imgUrl) {
        var img = document.createElement('img');
        /* Trebuie sa fie IDENTIC cu ce randeaza `snippets/cart-drawer.liquid`:
           144x216 (2:3, proportia masterelor) intr-o cutie CSS de 72x108, iar
           `width`/`height` sunt hintul de proportie care tine CLS-ul la zero.
           Calea asta reconstruieste randul dupa FIECARE mutatie de cos, deci o
           valoare ramasa in urma nu e un caz-limita: e cazul obisnuit. */
        img.src = imgUrl + (imgUrl.indexOf('?') === -1 ? '?' : '&') + 'width=144&height=216&crop=center';
        img.alt = item.product_title || '';
        img.width = 72;
        img.height = 108;
        img.loading = 'lazy';
        imgLink.appendChild(img);
      } else {
        var imgPlaceholder = document.createElement('div');
        imgPlaceholder.className = 'noimg';
        imgLink.appendChild(imgPlaceholder);
      }
      miniDiv.appendChild(imgLink);

      /* Info column */
      var infoDiv = document.createElement('div');
      infoDiv.className = 'mini__info';

      var topDiv = document.createElement('div');
      topDiv.className = 'mini__top';

      var h4 = document.createElement('h4');
      var titleLink = document.createElement('a');
      titleLink.href = item.url || '#';
      titleLink.textContent = item.product_title || '';
      h4.appendChild(titleLink);
      topDiv.appendChild(h4);

      var priceDiv = document.createElement('div');
      priceDiv.className = 'lprice';
      if (isGift) {
        var freeSpan = document.createElement('span');
        freeSpan.className = 'lprice__free';
        freeSpan.textContent = t('free', 'Free');
        priceDiv.appendChild(freeSpan);
      } else {
        if (item.original_line_price !== item.final_line_price) {
          var sEl = document.createElement('s');
          setMoney(sEl, item.original_line_price);
          priceDiv.appendChild(sEl);
          priceDiv.appendChild(document.createTextNode(' '));
        }
        priceDiv.insertAdjacentHTML('beforeend', formatMoney(item.final_line_price));
      }
      topDiv.appendChild(priceDiv);
      infoDiv.appendChild(topDiv);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        var varP = document.createElement('p');
        varP.className = 'v';
        varP.textContent = item.variant_title;
        infoDiv.appendChild(varP);
      }

      /* Line-level discounts */
      (item.line_level_discount_allocations || []).forEach(function (alloc) {
        var discP = document.createElement('p');
        discP.className = 'v';
        var title = alloc.discount_application && alloc.discount_application.title;
        discP.textContent = (title || '') + ' (−';
        discP.insertAdjacentHTML('beforeend', formatMoney(alloc.amount));
        discP.appendChild(document.createTextNode(')'));
        infoDiv.appendChild(discP);
      });

      if (!isGift) {
        var ctrlDiv = document.createElement('div');
        ctrlDiv.className = 'mini__ctrl';

        var qtyCtrl = document.createElement('div');
        qtyCtrl.className = 'qty-ctrl';

        var btnDown = document.createElement('button');
        btnDown.className = 'qty-btn';
        btnDown.setAttribute('data-cart-qty', 'down');
        btnDown.setAttribute('data-line-key', item.key);
        btnDown.setAttribute('data-line-qty', String(item.quantity));
        btnDown.setAttribute('aria-label', t('qty_decrease', 'Decrease quantity for {{ product }}', { product: item.product_title || '' }));
        btnDown.textContent = '−';

        var qtySpan = document.createElement('span');
        qtySpan.textContent = String(item.quantity);

        var btnUp = document.createElement('button');
        btnUp.className = 'qty-btn';
        btnUp.setAttribute('data-cart-qty', 'up');
        btnUp.setAttribute('data-line-key', item.key);
        btnUp.setAttribute('data-line-qty', String(item.quantity));
        btnUp.setAttribute('aria-label', t('qty_increase', 'Increase quantity for {{ product }}', { product: item.product_title || '' }));
        btnUp.textContent = '+';

        qtyCtrl.appendChild(btnDown);
        qtyCtrl.appendChild(qtySpan);
        qtyCtrl.appendChild(btnUp);
        ctrlDiv.appendChild(qtyCtrl);

        var removeBtn = document.createElement('button');
        removeBtn.className = 'rm';
        removeBtn.setAttribute('data-cart-remove', '');
        removeBtn.setAttribute('data-line-key', item.key);
        removeBtn.textContent = t('remove', 'Remove');
        ctrlDiv.appendChild(removeBtn);

        infoDiv.appendChild(ctrlDiv);
      }

      miniDiv.appendChild(infoDiv);
      db.appendChild(miniDiv);
    });

    if (recs) db.appendChild(recs);
  }

  /* ─── Rebuild drawer footer ────────────────────────────────────────────── */
  function rebuildDrawerFooter(cart) {
    var subtotalEl = document.getElementById('amphr-cart-subtotal');
    if (subtotalEl) {
      // total_price matches what Liquid renders server-side (cart-level
      // discounts included) so the value never "jumps" after an AJAX update.
      setMoney(subtotalEl, cart.total_price || 0);
    }
  }

  /* ─── Update title ─────────────────────────────────────────────────────── */
  /* The count moved out of the heading text and into its own bubble, so writing
     to the h3's textContent would delete the bubble element itself. */
  function updateTitle(cart) {
    var bubble = $('#amphr-cart .dh .cbub');
    if (!bubble) return;
    bubble.textContent = cart.item_count || 0;
    bubble.hidden = !cart.item_count;
  }

  /* ─── Update header badge ──────────────────────────────────────────────── */
  function updateBadge(cart) {
    var badge = document.getElementById('cart-count');
    if (!badge) return;
    badge.textContent = cart.item_count || '';
    badge.hidden = !cart.item_count;
  }

  /* ─── 1. Add to cart ───────────────────────────────────────────────────── */

  function addToCart(body, isFormData) {
    var opts = { method: 'POST', body: isFormData ? body : JSON.stringify(body) };
    if (!isFormData) opts.headers = { 'Content-Type': 'application/json' };
    return fetch(CART_ADD_URL, opts)
      .then(parseCartResponse)
      .then(function () { return refreshCart(); })
      .then(function () {
        if (window.amphr && typeof window.amphr.openDrawer === 'function') {
          window.amphr.openDrawer('#amphr-cart');
        }
      });
  }

  // 1a. Quick-add buttons on product cards
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    addToCart({ id: variantId, quantity: 1 })
      .catch(function (err) { showError(btn, err.message); })
      .finally(function () {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      });
  });

  // 1b. PDP product form submit
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.querySelector('#amphr-add')) return;

    e.preventDefault();

    var addBtn = form.querySelector('#amphr-add');
    if (addBtn) {
      if (addBtn.disabled) return;
      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
    }

    addToCart(new FormData(form), true)
      .catch(function (err) {
        if (addBtn) showError(addBtn, err.message);
      })
      .finally(function () {
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.removeAttribute('aria-busy');
        }
      });
  });

  /* ─── 2. Change qty / remove inside drawer ─────────────────────────────── */

  /* The controls used to be disabled while a mutation was in flight, so that a
     second click could not read a stale quantity. They stay live now: the new
     figure is written to the span before the request goes out, so the next
     click reads the up-to-date one. */

  document.addEventListener('click', function (e) {
    var cartEl = document.getElementById('amphr-cart');
    if (!cartEl) return;

    var target = e.target;

    // 2a. Remove item
    var removeBtn = target.closest('[data-cart-remove]');
    if (removeBtn && cartEl.contains(removeBtn)) {
      e.preventDefault();
      var lineKey = removeBtn.dataset.lineKey;
      if (!lineKey || removeBtn.disabled) return;

      /* The row goes now, not when the server answers. A round-trip is ~200ms
         on the live shop and more on a slow connection, and waiting for it made
         Remove feel broken. If the request fails, the catch in changeCartItem
         re-syncs from the server and the row comes back. */
      var doomedLine = removeBtn.closest('.mini');
      if (doomedLine) doomedLine.remove();

      changeCartItem(lineKey, 0, null);
      return;
    }

    // 2b. Qty up / down
    var qtyBtn = target.closest('[data-cart-qty]');
    if (qtyBtn && cartEl.contains(qtyBtn)) {
      e.preventDefault();
      var lineKey2 = qtyBtn.dataset.lineKey;
      var direction = qtyBtn.dataset.cartQty;
      if (!lineKey2 || !direction || qtyBtn.disabled) return;

      var span = qtyBtn.parentNode && qtyBtn.parentNode.querySelector('span');
      var currentQty = span ? parseInt(span.textContent, 10) : 1;
      if (isNaN(currentQty)) currentQty = 1;

      var newQty = direction === 'up' ? currentQty + 1 : Math.max(0, currentQty - 1);

      /* Show the new number straight away, same reasoning as Remove. The
         buttons stay live: the next click reads the figure from this span, so
         two quick taps still compute the right quantity, and the server's
         answer replaces the row either way. Stepping to zero empties the row,
         so that one goes immediately too. */
      if (span) span.textContent = String(newQty);
      if (newQty === 0) {
        var emptied = qtyBtn.closest('.mini');
        if (emptied) emptied.remove();
      }

      changeCartItem(lineKey2, newQty, null);
    }
  });

  /* One request in flight per line, carrying the latest quantity.

     /cart/change.js takes an absolute quantity, not a delta, so overlapping
     requests are decided by which one REACHES the server last, not which one
     the customer clicked last. Measured: five taps 80ms apart on "+" left the
     cart at 7 while the drawer showed 8 — one tap silently lost.

     Disabling the buttons during the request would prevent it, but that is what
     made the stepper feel sluggish. Instead a line with a request in flight
     records the quantity it wants; when the request returns, the newest wanted
     value is sent, and the values in between are dropped — they were never
     anything the customer wanted to keep. */
  var linePending = {};
  var lineBusy = {};

  function changeCartItem(lineKey, quantity, sourceEl) {
    if (lineBusy[lineKey]) {
      linePending[lineKey] = quantity;
      return lineBusy[lineKey];
    }

    lineBusy[lineKey] = sendLineChange(lineKey, quantity, sourceEl)
      .then(function () {
        delete lineBusy[lineKey];
        if (Object.prototype.hasOwnProperty.call(linePending, lineKey)) {
          var next = linePending[lineKey];
          delete linePending[lineKey];
          return changeCartItem(lineKey, next, sourceEl);
        }
      });

    return lineBusy[lineKey];
  }

  function sendLineChange(lineKey, quantity, sourceEl) {
    var mySeq = ++cartSeq;
    return fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    })
      .then(parseCartResponse)
      .then(function (cart) {
        // /cart/change.js returns the full cart — no second round-trip needed.
        applyCart(cart, mySeq);
      })
      .catch(function (err) {
        console.warn('[amphr-cart] changeCartItem error', err);
        /* The row the customer clicked is usually gone by now — it was removed
           the moment they clicked — so the message needs somewhere else to
           live. It goes at the top of the drawer body instead of nowhere. */
        var anchor = (sourceEl && document.contains(sourceEl))
          ? sourceEl
          : $('#amphr-cart .db');
        if (anchor) showError(anchor, err.message);
        // Re-sync UI with the server's actual state (e.g. stock limit hit)
        return refreshCart();
      });
  }

  /* ─── 3. Cart changes made by apps, not by us ──────────────────────────── */

  /* GiftPro adds and removes the free line itself, on its own request (tagged
     `?_giftpro=1`), roughly 1,8s after our mutation lands — measured. Our own
     cycle is finished long before that, so without this the drawer keeps
     showing the cart as it stood before the app touched it: the gift appears
     only after a page reload, and a gift the app has withdrawn stays on screen.

     The app announces the change on `document`, so we listen instead of
     polling. Its events carry a `detail.cart`, but that payload is the cart as
     it stood BEFORE the app's own line landed — measured: right after it added
     the gift it published item_count 2 while /cart.js already returned 3. So
     the event is used as a signal only, and the cart is read from the server.

     It fires three names in a row for the same change, hence the debounce —
     three refetches and three repaints would flicker.

     Listening for the events rather than for GiftPro specifically means any
     other cart app following the same convention is handled too. */
  var appCartTimer = null;

  function onAppCartEvent() {
    if (appCartTimer) clearTimeout(appCartTimer);
    appCartTimer = setTimeout(function () {
      appCartTimer = null;
      refreshCart();
    }, 80);
  }

  ['cart:updated', 'cart:refresh', 'theme:cart:refresh'].forEach(function (name) {
    document.addEventListener(name, onAppCartEvent);
  });

  /* ─── Public hooks (Standard Actions bridge, cart-recs, etc.) ──────────── */
  if (window.amphr) {
    window.amphr.refreshCart = refreshCart;
  }
  window.amphrCart = { refresh: refreshCart, applyCart: applyCart, formatMoney: formatMoney };
})();
